import { styled } from '@mui/material';
import type { ReactNode } from 'react';

import { typographyFontWeights, typographyLineHeights } from '../../../theme/tokens/typography';

type BrandLockupSize = 'small' | 'large';

type BrandLockupAlign = 'start' | 'center';

type Props = {
  /** Product name, e.g. "M. Eugène". */
  name: string;
  /** Decorative logo mark: an SVG drawn in `currentColor` (tinted with the primary color) or an image. */
  logo?: ReactNode;
  /** Short line under the name, e.g. the product slogan. */
  tagline?: string;
  /** `large` on entry screens (sign-in, space selection), `small` in the application sidebar. */
  size?: BrandLockupSize;
  /** `start`: mark beside the name. `center`: mark above the name, all centered (entry screens). */
  align?: BrandLockupAlign;
  'data-testid'?: string;
};

/** What a product hands to the frames that sign it (`EntryLayout`, `AppShell`). */
type BrandIdentity = Pick<Props, 'name' | 'logo' | 'tagline'>;

type OwnerState = {
  size: BrandLockupSize;
  align: BrandLockupAlign;
};

const MARK_HEIGHTS: Record<BrandLockupSize, number> = {
  small: 3,
  large: 5,
};

// Stacked, the mark leads alone and can take more height without widening the lockup.
const CENTERED_MARK_HEIGHT = 7;

const Root = styled('div')<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(ownerState.size === 'large' ? 1.5 : 1),
  minWidth: 0,
  ...(ownerState.align === 'center' && {
    flexDirection: 'column',
    gap: theme.spacing(1),
    textAlign: 'center',
  }),
}));

const Mark = styled('span')<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  display: 'flex',
  flexShrink: 0,
  height: theme.spacing(ownerState.align === 'center' ? CENTERED_MARK_HEIGHT : MARK_HEIGHTS[ownerState.size]),
  color: theme.palette.primary.main,
  '& > svg, & > img': {
    display: 'block',
    width: 'auto',
    height: '100%',
  },
}));

const Text = styled('span')({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  maxWidth: '100%',
});

const Name = styled('span')<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  ...(ownerState.size === 'large' ? theme.typography.h4 : theme.typography.body1),
  fontWeight: typographyFontWeights.extraBold,
  lineHeight: typographyLineHeights.tight,
  letterSpacing: '-0.01em',
  color: theme.palette.text.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const Tagline = styled('span')(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.text.secondary,
}));

/** Product signature: logo mark, name and optional slogan, in the primary color of the theme. */
export function BrandLockup(props: Props) {
  const ownerState = { size: props.size ?? 'large', align: props.align ?? 'start' };

  return (
    <Root ownerState={ownerState} data-testid={props['data-testid'] ?? 'brand-lockup'}>
      {props.logo ? (
        <Mark ownerState={ownerState} aria-hidden="true">
          {props.logo}
        </Mark>
      ) : null}
      <Text>
        <Name ownerState={ownerState}>{props.name}</Name>
        {props.tagline ? <Tagline>{props.tagline}</Tagline> : null}
      </Text>
    </Root>
  );
}

export type { BrandIdentity, Props as BrandLockupProps };
